import React, { useRef } from 'react';
import Spinner from './Spinner';

// Custom paginated server-side table — no PrimeReact dependency.
// loadLazyTimeout uses useRef so timeout cancellation is correct across renders.
export default function DataTable({
  columns,
  data,
  total,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  loading,
  emptyMessage = 'No records found.',
  actions,
  selectable = false,
  selected = [],
  onSelectAll,
  onSelectRow,
}) {
  const totalPages = Math.ceil(total / perPage) || 1;
  const start = page * perPage + 1;
  const end   = Math.min((page + 1) * perPage, total);

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.08]">
              {selectable && (
                <th className="table-header w-10">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-white/20 bg-bg-input accent-accent cursor-pointer"
                    checked={data?.length > 0 && selected.length === data?.length}
                    onChange={e => onSelectAll?.(e.target.checked)}
                  />
                </th>
              )}
              {columns.map(col => (
                <th key={col.key} className={`table-header ${col.className ?? ''}`} style={col.style}>
                  {col.header}
                </th>
              ))}
              {actions && <th className="table-header w-24">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)}
                  className="table-cell text-center py-12">
                  <div className="flex items-center justify-center gap-2 text-text-muted">
                    <Spinner size="sm" /> Loading…
                  </div>
                </td>
              </tr>
            ) : !data?.length ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)}
                  className="table-cell text-center py-12 text-text-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr key={row.id ?? i} className="table-row">
                  {selectable && (
                    <td className="table-cell">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-white/20 bg-bg-input accent-accent cursor-pointer"
                        checked={selected.includes(row.id)}
                        onChange={e => onSelectRow?.(row.id, e.target.checked)}
                      />
                    </td>
                  )}
                  {columns.map(col => (
                    <td key={col.key} className={`table-cell ${col.cellClass ?? ''}`} style={col.style}>
                      {col.render ? col.render(row) : row[col.key] ?? '—'}
                    </td>
                  ))}
                  {actions && <td className="table-cell">{actions(row)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.08]">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Show</span>
          <select
            value={perPage}
            onChange={e => onPerPageChange?.(Number(e.target.value))}
            className="bg-bg-input border border-white/[0.08] rounded px-2 py-1 text-text-main text-xs"
          >
            {[15, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>per page • {total > 0 ? `${start}–${end} of ${total}` : '0 records'}</span>
        </div>
        <div className="flex items-center gap-1">
          <PageBtn disabled={page === 0} onClick={() => onPageChange(0)}>«</PageBtn>
          <PageBtn disabled={page === 0} onClick={() => onPageChange(page - 1)}>‹</PageBtn>
          {getPageNumbers(page, totalPages).map((n, i) =>
            n === '…' ? <span key={i} className="px-2 text-text-muted text-xs">…</span> : (
              <PageBtn key={n} active={n - 1 === page} onClick={() => onPageChange(n - 1)}>{n}</PageBtn>
            )
          )}
          <PageBtn disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>›</PageBtn>
          <PageBtn disabled={page >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>»</PageBtn>
        </div>
      </div>
    </div>
  );
}

function PageBtn({ children, onClick, disabled, active }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-2.5 py-1 rounded text-xs transition-colors duration-100 ${
        active
          ? 'bg-accent text-white'
          : disabled
          ? 'text-text-muted cursor-not-allowed opacity-40'
          : 'text-text-muted hover:text-text-main hover:bg-white/[0.06]'
      }`}
    >
      {children}
    </button>
  );
}

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 2)  pages.push('…');
  for (let i = Math.max(2, current); i <= Math.min(current + 2, total - 1); i++) pages.push(i);
  if (current < total - 3) pages.push('…');
  pages.push(total);
  return pages;
}

// Hook for server-side paginated tables — fixes artificial delay and useRef timeout
export function useServerTable(fetcher, deps = []) {
  const [data, setData]         = React.useState([]);
  const [total, setTotal]       = React.useState(0);
  const [page, setPage]         = React.useState(0);
  const [perPage, setPerPage]   = React.useState(15);
  const [loading, setLoading]   = React.useState(false);
  const [filters, setFilters]   = React.useState({});
  const [sortField, setSortField] = React.useState('created_at');
  const [sortOrder, setSortOrder] = React.useState('desc');
  const timerRef = React.useRef(null);

  const load = React.useCallback(() => {
    setLoading(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetcher({ page, perPage, filters, sortField, sortOrder });
        setData(res.data ?? []);
        setTotal(res.pagination?.total ?? 0);
      } catch {
        // error handled by axios interceptor
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [fetcher, page, perPage, filters, sortField, sortOrder, ...deps]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return {
    data, total, page, setPage, perPage, setPerPage,
    loading, filters, setFilters,
    sortField, setSortField, sortOrder, setSortOrder,
    refresh: load,
  };
}
